import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    InputGroup, Form, ProgressBar, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";


export default function StockCritique() {
    const navigate = useNavigate();
    const [produits, setProduits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filterType, setFilterType] = useState('tous');
    const [sortConfig, setSortConfig] = useState({ key: 'urgence', direction: 'desc' });

    useEffect(() => {
        fetchStockCritique();
    }, []);

    const fetchStockCritique = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/stock_etat.php?action=critique`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setProduits(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getNiveauAlerte = (niveau) => {
        if (niveau === 'rupture') {
            return (
                <span className="status-badge" style={{ background: '#ef444415', color: '#ef4444' }}>
                    <i className="ri-close-circle-line me-1 fs-10"></i>
                    Rupture
                </span>
            );
        }
        return (
            <span className="status-badge" style={{ background: '#f59e0b15', color: '#f59e0b' }}>
                <i className="ri-alert-line me-1 fs-10"></i>
                Critique
            </span>
        );
    };

    const getUrgenceLevel = (produit) => {
        if (produit.niveau_alerte === 'rupture') return 3;
        if (produit.quantite_reelle <= produit.seuil_alerte / 2) return 2;
        return 1;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const filteredProduits = produits
        .filter(p => {
            const matchesSearch = p.reference?.toLowerCase().includes(filterText.toLowerCase()) ||
                p.designation?.toLowerCase().includes(filterText.toLowerCase());
            
            if (filterType === 'rupture') return p.niveau_alerte === 'rupture' && matchesSearch;
            if (filterType === 'critique') return p.niveau_alerte === 'critique' && matchesSearch;
            return matchesSearch;
        })
        .sort((a, b) => {
            if (sortConfig.key === 'urgence') {
                const niveauA = getUrgenceLevel(a);
                const niveauB = getUrgenceLevel(b);
                return sortConfig.direction === 'asc' ? niveauA - niveauB : niveauB - niveauA;
            }
            if (sortConfig.key === 'quantite_reelle') {
                return sortConfig.direction === 'asc' 
                    ? (a.quantite_reelle || 0) - (b.quantite_reelle || 0)
                    : (b.quantite_reelle || 0) - (a.quantite_reelle || 0);
            }
            if (sortConfig.key === 'designation') {
                return sortConfig.direction === 'asc'
                    ? a.designation?.localeCompare(b.designation)
                    : b.designation?.localeCompare(a.designation);
            }
            return 0;
        });

    const totalRupture = produits.filter(p => p.niveau_alerte === 'rupture').length;
    const totalCritique = produits.filter(p => p.niveau_alerte === 'critique').length;
    const valeurTotaleRupture = produits
        .filter(p => p.niveau_alerte === 'rupture')
        .reduce((sum, p) => sum + (p.quantite_reelle * p.prix_achat_ht || 0), 0);

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Analyse des stocks critiques...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchStockCritique}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-alert-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Stock critique</h4>
                                    <p className="text-muted small mb-0">
                                        {produits.length} produit(s) nécessitent une attention
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/stock/etat/instantane')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    <Row className="g-3 mb-4">
                        <Col sm={6} xl={4}>
                            <div className="kpi-card kpi-danger">
                                <div className="kpi-icon">
                                    <i className="ri-close-circle-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Rupture de stock</span>
                                    <h3 className="kpi-value">{totalRupture}</h3>
                                    <span className="kpi-unit">produits épuisés</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={4}>
                            <div className="kpi-card kpi-warning">
                                <div className="kpi-icon">
                                    <i className="ri-alert-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Stock critique</span>
                                    <h3 className="kpi-value">{totalCritique}</h3>
                                    <span className="kpi-unit">sous le seuil d'alerte</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={4}>
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon">
                                    <i className="ri-money-euro-circle-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Valeur en rupture</span>
                                    <h3 className="kpi-value" style={{ fontSize: '1rem' }}>{formatCurrency(valeurTotaleRupture)}</h3>
                                    <span className="kpi-unit">capital immobilisé</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Filtres modernes */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md={5}>
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher par référence, produit..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <Form.Select 
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                        className="rounded-pill"
                                    >
                                        <option value="tous">Toutes les alertes</option>
                                        <option value="rupture">Rupture</option>
                                        <option value="critique">Critique</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Select 
                                        value={sortConfig.key}
                                        onChange={(e) => setSortConfig({ key: e.target.value, direction: 'desc' })}
                                        className="rounded-pill"
                                    >
                                        <option value="urgence">Trier par urgence</option>
                                        <option value="quantite_reelle">Trier par stock</option>
                                        <option value="designation">Trier par nom</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={() => {
                                            setFilterText('');
                                            setFilterType('tous');
                                        }} 
                                        className="w-100 rounded-pill"
                                    >
                                        <i className="ri-refresh-line me-2"></i>Réinitialiser
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des produits critiques */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">Référence</th>
                                            <SortableHeader field="designation">Produit</SortableHeader>
                                            <th>Catégorie</th>
                                            <SortableHeader field="quantite_reelle" className="text-center">Stock actuel</SortableHeader>
                                            <th className="text-center">Seuil</th>
                                            <th className="text-center">Manquant</th>
                                            <th>Valeur</th>
                                            <th>Niveau</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredProduits.length > 0 ? (
                                            filteredProduits.map((p, index) => {
                                                const ratio = (p.quantite_reelle / p.seuil_alerte) * 100;
                                                const urgenceLevel = getUrgenceLevel(p);
                                                return (
                                                    <tr key={p.id_produit} className="critique-row" style={{
                                                        backgroundColor: urgenceLevel === 3 ? '#fff5f5' : 
                                                                       urgenceLevel === 2 ? '#fffbeb' : 'transparent'
                                                    }}>
                                                        <td className="ps-4">
                                                            <span className="reference-badge">{p.reference}</span>
                                                       </td>
                                                        <td>
                                                            <Link to={`/stock/produits/${p.id_produit}`} className="table-link">
                                                                {p.designation}
                                                            </Link>
                                                       </td>
                                                        <td>{p.nom_categorie || <span className="text-muted">-</span>}</td>
                                                        <td className="text-center" style={{ minWidth: '140px' }}>
                                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={<Tooltip>Niveau: {Math.min(ratio, 100).toFixed(0)}% du seuil</Tooltip>}
                                                                >
                                                                    <div className="progress-bar-custom" style={{ width: '80px' }}>
                                                                        <div className="progress-fill" style={{ width: `${Math.min(ratio, 100)}%`, background: p.niveau_alerte === 'rupture' ? '#ef4444' : '#f59e0b' }}></div>
                                                                    </div>
                                                                </OverlayTrigger>
                                                                <span className={`fw-bold ${p.niveau_alerte === 'rupture' ? 'text-danger' : ''}`}>
                                                                    {p.quantite_reelle}
                                                                </span>
                                                            </div>
                                                       </td>
                                                        <td className="text-center">
                                                            <Badge className="rounded-pill px-3" style={{ background: '#64748b' }}>
                                                                {p.seuil_alerte}
                                                            </Badge>
                                                   </td>
                                                        <td className="text-center">
                                                            {p.quantite_a_commander > 0 ? (
                                                                <span className="fw-bold text-danger">
                                                                    <i className="ri-arrow-down-line me-1"></i>
                                                                    {p.quantite_a_commander}
                                                                </span>
                                                            ) : <span className="text-muted">-</span>}
                                                   </td>
                                                        <td className="fw-bold text-primary">
                                                            {formatCurrency(p.quantite_reelle * p.prix_achat_ht)}
                                                    </td>
                                                        <td>{getNiveauAlerte(p.niveau_alerte)}</td>
                                                        <td className="text-center pe-4">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Réapprovisionner</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-primary" 
                                                                    size="sm"
                                                                    className="rounded-pill px-3 d-inline-flex align-items-center gap-2"
                                                                    style={{ borderColor: '#21ACCD', color: '#21ACCD' }}
                                                                    onClick={() => navigate(`/stock/mouvements/ajouter/${p.id_produit}?type=entree`)}
                                                                >
                                                                    <i className="ri-add-line"></i>
                                                                    <span>Commander</span>
                                                                </Button>
                                                            </OverlayTrigger>
                                                  </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-checkbox-circle-line empty-icon text-success" style={{ fontSize: '4rem' }}></i>
                                                        <h5 className="mt-3">Aucun produit en stock critique</h5>
                                                        <p className="text-muted mb-3">
                                                            {filterText || filterType !== 'tous' 
                                                                ? "Aucun résultat ne correspond à vos filtres"
                                                                : "Tous les stocks sont à un niveau satisfaisant"}
                                                        </p>
                                                        {(filterText || filterType !== 'tous') && (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => {
                                                                    setFilterText('');
                                                                    setFilterType('tous');
                                                                }}
                                                                className="rounded-pill"
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                            </Button>
                                                        )}
                                                    </div>
                                               </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Actions recommandées */}
                    {filteredProduits.length > 0 && (
                        <Card className="border-0 shadow-sm rounded-3 mt-4" style={{ background: '#f8fafc' }}>
                            <Card.Body className="p-4">
                                <h6 className="mb-3 fw-semibold">
                                    <i className="ri-lightbulb-line me-2 text-warning"></i>
                                    Actions recommandées
                                </h6>
                                <Row className="g-3">
                                    <Col md={4}>
                                        <div className="d-flex align-items-center p-3 bg-white rounded-3">
                                            <div className="bg-danger bg-opacity-10 rounded-circle p-3 me-3">
                                                <i className="ri-shopping-cart-line text-danger fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Commandes urgentes</small>
                                                <strong className="fs-5">{totalRupture + totalCritique}</strong>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="d-flex align-items-center p-3 bg-white rounded-3">
                                            <div className="bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                                                <i className="ri-truck-line text-warning fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Montant à commander</small>
                                                <strong className="fs-5">{formatCurrency(valeurTotaleRupture)}</strong>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="d-flex align-items-center p-3 bg-white rounded-3">
                                            <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                                                <i className="ri-file-copy-line text-primary fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Commandes groupées</small>
                                                <strong className="fs-5">Créer</strong>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </div>
                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-danger { border-left-color: #ef4444; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-primary { border-left-color: #21ACCD; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Reference Badge */
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                    display: inline-block;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Progress Bar */
                .progress-bar-custom {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 6px;
                    overflow: hidden;
                }
                .progress-fill {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .critique-row {
                    transition: all 0.2s ease;
                }
                
                .critique-row:hover {
                    background: #f8fafc !important;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-14 {
                    font-size: 0.875rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
            `}</style>
        </>
    );
}